import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faPen,
  faTrash,
  faSlidersH,
  faUserPlus,
  faUserShield,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../config";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const PRN_SEARCH_MAX_REFERENCES_SETTING_KEY =
  "provider_payment_prn_search_max_references_per_search";
const LOA_BYPASS_ENDPOINT = `${BASE_URL}/system-settings-v2/loa-approval-bypass-users`;
const ENROLLMENT_INTEGER_SETTINGS = {
  member_enrollment_max_rows: "Maximum data rows per enrollment file, excluding the header. Default: 300,000.",
  member_enrollment_max_file_size_mb: "Maximum size per enrollment file in MB. Default: 100 MB. The server must also allow this upload size.",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatOauthUserName = (user) => {
  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return fullName || user?.full_name || user?.username || `OAuth User #${user?.id}`;
};

const SystemSettingsV2 = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    description: "",
  });
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [bypassRows, setBypassRows] = useState([]);
  const [bypassSearch, setBypassSearch] = useState("");
  const [bypassQuery, setBypassQuery] = useState("");
  const [bypassActiveFilter, setBypassActiveFilter] = useState("1");
  const [isBypassLoading, setIsBypassLoading] = useState(false);
  const [bypassError, setBypassError] = useState("");
  const [bypassSuccess, setBypassSuccess] = useState("");
  const [bypassRefreshKey, setBypassRefreshKey] = useState(0);
  const [oauthUserSearch, setOauthUserSearch] = useState("");
  const [oauthUserResults, setOauthUserResults] = useState([]);
  const [isOauthUserSearching, setIsOauthUserSearching] = useState(false);
  const [selectedOauthUser, setSelectedOauthUser] = useState(null);
  const [bypassRemarks, setBypassRemarks] = useState("");
  const [isBypassSaving, setIsBypassSaving] = useState(false);

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("authentication/token");
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const fetchBypassUsers = async () => {
      setIsBypassLoading(true);
      setBypassError("");
      try {
        const authHeaders = getAuthHeaders();
        if (!authHeaders) {
          setBypassError("Missing access token.");
          return;
        }

        const bypassParams = {
          search: bypassQuery,
          page: 1,
          per_page: 50,
        };
        if (bypassActiveFilter !== "") {
          bypassParams.active = bypassActiveFilter;
        }

        const response = await axios.get(LOA_BYPASS_ENDPOINT, {
          headers: authHeaders,
          params: bypassParams,
        });

        if (response.data?.status !== "success") {
          setBypassError(
            response.data?.message || "Failed to load LOA approval bypass users."
          );
          return;
        }

        setBypassRows(response.data?.data?.rows || []);
      } catch (error) {
        setBypassError(
          error.response?.data?.message || "Failed to load LOA approval bypass users."
        );
      } finally {
        setIsBypassLoading(false);
      }
    };

    fetchBypassUsers();
  }, [bypassQuery, bypassActiveFilter, bypassRefreshKey]);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("authentication/token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await axios.get(`${BASE_URL}/system-settings-v2`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            search: query,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load system settings.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      } catch (error) {
        setLoadError("Failed to load system settings.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [query, page, perPage, refreshKey]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleOpenCreate = () => {
    setFormData({
      key: "",
      value: "",
      description: "",
    });
    setFormError("");
    setFormSuccess("");
    setIsCreateOpen(true);
  };

  const handleOpenEdit = (row) => {
    setActiveRow(row);
    setFormData({
      key: row.key || "",
      value: row.value || "",
      description: row.description || "",
    });
    setFormError("");
    setFormSuccess("");
    setIsEditOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateOpen(false);
    setIsEditOpen(false);
    setActiveRow(null);
    setIsSaving(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setFormError("");
    setFormSuccess("");

    if (!formData.key.trim()) {
      setFormError("Key is required.");
      setIsSaving(false);
      return;
    }

    if (
      (formData.key.trim() === PRN_SEARCH_MAX_REFERENCES_SETTING_KEY ||
        Object.prototype.hasOwnProperty.call(ENROLLMENT_INTEGER_SETTINGS, formData.key.trim())) &&
      !/^[1-9]\d*$/.test(String(formData.value ?? "").trim())
    ) {
      setFormError("Value must be a whole number greater than 0.");
      setIsSaving(false);
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(ENROLLMENT_INTEGER_SETTINGS, formData.key.trim()) &&
      Number(formData.value) > 2147483647
    ) {
      setFormError("Value must not exceed 2147483647.");
      setIsSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem("authentication/token");
      if (!token) {
        setFormError("Missing access token.");
        return;
      }

      if (isCreateOpen) {
        const response = await axios.post(
          `${BASE_URL}/system-settings-v2`,
          {
            key: formData.key.trim(),
            value: formData.value?.toString() || "",
            description: formData.description?.trim() || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.status !== "success") {
          const message = response.data?.message || "Failed to create setting.";
          if (message && typeof message === "object") {
            setFormError(Object.values(message).flat().join(" "));
          } else {
            setFormError(message);
          }
          return;
        }

        setFormSuccess("Setting created.");
        setIsCreateOpen(false);
      }

      if (isEditOpen && activeRow?.id) {
        const response = await axios.put(
          `${BASE_URL}/system-settings-v2/${activeRow.id}`,
          {
            key: formData.key.trim(),
            value: formData.value?.toString() || "",
            description: formData.description?.trim() || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.status !== "success") {
          const message = response.data?.message || "Failed to update setting.";
          if (message && typeof message === "object") {
            setFormError(Object.values(message).flat().join(" "));
          } else {
            setFormError(message);
          }
          return;
        }

        setFormSuccess("Setting updated.");
        setIsEditOpen(false);
      }

      setPage(1);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setFormError("Failed to save setting.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmDelete = window.confirm(`Delete setting "${row.key}"?`);
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("authentication/token");
      if (!token) {
        setLoadError("Missing access token.");
        return;
      }

      const response = await axios.delete(
        `${BASE_URL}/system-settings-v2/${row.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.status !== "success") {
        setLoadError(response.data?.message || "Failed to delete setting.");
        return;
      }

      setPage(1);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setLoadError("Failed to delete setting.");
    }
  };

  const handleBypassSearchSubmit = (event) => {
    event.preventDefault();
    setBypassQuery(bypassSearch.trim());
  };

  const handleOauthUserSearch = async (event) => {
    event.preventDefault();
    setIsOauthUserSearching(true);
    setBypassError("");
    setBypassSuccess("");
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) {
        setBypassError("Missing access token.");
        return;
      }

      const response = await axios.get(`${LOA_BYPASS_ENDPOINT}/oauth-users`, {
        headers: authHeaders,
        params: {
          search: oauthUserSearch.trim(),
          per_page: 10,
        },
      });

      if (response.data?.status !== "success") {
        setBypassError(response.data?.message || "Failed to search OAuth users.");
        return;
      }

      setOauthUserResults(response.data?.data || []);
    } catch (error) {
      setBypassError(error.response?.data?.message || "Failed to search OAuth users.");
    } finally {
      setIsOauthUserSearching(false);
    }
  };

  const handleAddBypassUser = async () => {
    if (!selectedOauthUser?.id) {
      setBypassError("Select an OAuth user first.");
      return;
    }

    setIsBypassSaving(true);
    setBypassError("");
    setBypassSuccess("");
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) {
        setBypassError("Missing access token.");
        return;
      }

      const response = await axios.post(
        LOA_BYPASS_ENDPOINT,
        {
          oauth_user_id: Number(selectedOauthUser.id),
          remarks:
            bypassRemarks.trim() ||
            "Allowed to bypass LOA approval for all coverages.",
        },
        { headers: authHeaders }
      );

      if (response.data?.status !== "success") {
        setBypassError(
          response.data?.message || "Failed to add LOA approval bypass user."
        );
        return;
      }

      setBypassSuccess("User added to LOA approval bypass.");
      setSelectedOauthUser(null);
      setOauthUserSearch("");
      setOauthUserResults([]);
      setBypassRemarks("");
      setBypassActiveFilter("1");
      setBypassRefreshKey((prev) => prev + 1);
    } catch (error) {
      setBypassError(
        error.response?.data?.message || "Failed to add LOA approval bypass user."
      );
    } finally {
      setIsBypassSaving(false);
    }
  };

  const handleUpdateBypassStatus = async (row, activeFlag) => {
    setBypassError("");
    setBypassSuccess("");
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) {
        setBypassError("Missing access token.");
        return;
      }

      const response = await axios.put(
        `${LOA_BYPASS_ENDPOINT}/${row.id}`,
        {
          active_flag: activeFlag,
          remarks:
            row.remarks ||
            "Allowed to bypass LOA approval for all coverages.",
        },
        { headers: authHeaders }
      );

      if (response.data?.status !== "success") {
        setBypassError(
          response.data?.message || "Failed to update LOA approval bypass user."
        );
        return;
      }

      setBypassSuccess(activeFlag ? "Bypass user activated." : "Bypass user deactivated.");
      setBypassRefreshKey((prev) => prev + 1);
    } catch (error) {
      setBypassError(
        error.response?.data?.message || "Failed to update LOA approval bypass user."
      );
    }
  };

  const handleDeactivateBypassUser = async (row) => {
    const confirmRemove = window.confirm(
      `Remove LOA approval bypass for ${row.username || `OAuth User #${row.oauth_user_id}`}?`
    );
    if (!confirmRemove) return;

    setBypassError("");
    setBypassSuccess("");
    try {
      const authHeaders = getAuthHeaders();
      if (!authHeaders) {
        setBypassError("Missing access token.");
        return;
      }

      const response = await axios.delete(`${LOA_BYPASS_ENDPOINT}/${row.id}`, {
        headers: authHeaders,
      });

      if (response.data?.status !== "success") {
        setBypassError(
          response.data?.message || "Failed to remove LOA approval bypass user."
        );
        return;
      }

      setBypassSuccess("User removed from active LOA approval bypass.");
      setBypassRefreshKey((prev) => prev + 1);
    } catch (error) {
      setBypassError(
        error.response?.data?.message || "Failed to remove LOA approval bypass user."
      );
    }
  };

  const isPrnSearchMaxReferencesSetting =
    formData.key.trim() === PRN_SEARCH_MAX_REFERENCES_SETTING_KEY;
  const enrollmentSettingHelp = Object.prototype.hasOwnProperty.call(ENROLLMENT_INTEGER_SETTINGS, formData.key.trim())
    ? ENROLLMENT_INTEGER_SETTINGS[formData.key.trim()]
    : null;
  const isPositiveIntegerSetting = isPrnSearchMaxReferencesSetting || Boolean(enrollmentSettingHelp);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
              <FontAwesomeIcon icon={faSlidersH} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">
                System Settings V2
              </h1>
              <p className="text-sm text-slate-600">
                Manage configuration keys and runtime settings for the platform.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-64">
              <FontAwesomeIcon
                icon={faSearch}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                type="text"
                placeholder="Search settings"
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
              />
            </form>
            <button
              type="button"
              onClick={handleSearchSubmit}
              className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => {
                setSearch("member_enrollment_");
                setQuery("member_enrollment_");
                setPage(1);
              }}
              className="rounded-2xl border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50"
            >
              Batch Enrollment Limits
            </button>
            <button
              type="button"
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-100 transition hover:bg-indigo-700"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Setting
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {loadError && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 text-sm text-slate-500">
            <span>{isLoading ? "Loading settings..." : `${total} setting(s)`}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Rows
              </span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-xl border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
                <tr>
                  <th className="px-6 py-4 text-left">Key</th>
                  <th className="px-6 py-4 text-left">Value</th>
                  <th className="px-6 py-4 text-left">Description</th>
                  <th className="px-6 py-4 text-left">Updated</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {row.key}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      <div className="max-w-[320px] truncate">{row.value}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      <div className="max-w-[320px] truncate">
                        {row.description || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      {formatDateTime(row.updated_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(row)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        >
                          <FontAwesomeIcon icon={faPen} />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      No settings found.
                    </td>
                  </tr>
                )}
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      Loading settings...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-sm text-slate-500">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    LOA Approval Bypass Users
                  </h2>
                  <p className="mt-1 max-w-3xl text-sm text-slate-600">
                    Users listed here can generate LOA transactions for all coverages
                    without pre-audit approval. Existing pending approval rows are not changed.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setBypassRefreshKey((prev) => prev + 1)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                <FontAwesomeIcon icon={faRotateRight} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid gap-6 p-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)]">
            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">
                Add OAuth User
              </h3>
              <form onSubmit={handleOauthUserSearch} className="mt-4 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={oauthUserSearch}
                    onChange={(event) => setOauthUserSearch(event.target.value)}
                    type="text"
                    placeholder="Search id, username, name, or email"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isOauthUserSearching}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-100 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faSearch} />
                  {isOauthUserSearching ? "Searching..." : "Search"}
                </button>
              </form>

              <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
                {oauthUserResults.map((user) => {
                  const isSelected = selectedOauthUser?.id === user.id;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => setSelectedOauthUser(user)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition ${
                        isSelected
                          ? "border-indigo-300 bg-indigo-50 text-indigo-900"
                          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold">
                          {formatOauthUserName(user)}
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                          OAuth #{user.id}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {user.username} {user.email ? `- ${user.email}` : ""}
                      </div>
                      {Number(user.loa_approval_bypass) === 1 && (
                        <div className="mt-2 text-xs font-semibold text-emerald-700">
                          Already active for LOA approval bypass
                        </div>
                      )}
                    </button>
                  );
                })}
                {!isOauthUserSearching && oauthUserResults.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                    Search OAuth users to add an exempt user.
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Remarks
                </label>
                <textarea
                  value={bypassRemarks}
                  onChange={(event) => setBypassRemarks(event.target.value)}
                  rows={3}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="Optional note for this bypass setting"
                />
              </div>

              <button
                type="button"
                onClick={handleAddBypassUser}
                disabled={isBypassSaving || !selectedOauthUser?.id}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-100 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FontAwesomeIcon icon={faUserPlus} />
                {isBypassSaving ? "Saving..." : "Add to LOA Bypass"}
              </button>
            </div>

            <div>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <form onSubmit={handleBypassSearchSubmit} className="flex flex-1 flex-col gap-3 sm:flex-row">
                  <div className="relative flex-1">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      value={bypassSearch}
                      onChange={(event) => setBypassSearch(event.target.value)}
                      type="text"
                      placeholder="Search current bypass users"
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    Search
                  </button>
                </form>
                <select
                  value={bypassActiveFilter}
                  onChange={(event) => setBypassActiveFilter(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                >
                  <option value="1">Active bypass</option>
                  <option value="0">Inactive bypass</option>
                  <option value="">All bypass rows</option>
                </select>
              </div>

              {(bypassError || bypassSuccess) && (
                <div
                  className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
                    bypassError
                      ? "border-rose-100 bg-rose-50 text-rose-600"
                      : "border-emerald-100 bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {bypassError || bypassSuccess}
                </div>
              )}

              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
                      <tr>
                        <th className="px-4 py-3 text-left">OAuth User</th>
                        <th className="px-4 py-3 text-left">Coverage</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Updated</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {isBypassLoading ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                            Loading LOA bypass users...
                          </td>
                        </tr>
                      ) : bypassRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                            No LOA bypass users found.
                          </td>
                        </tr>
                      ) : (
                        bypassRows.map((row) => (
                          <tr key={row.id} className="hover:bg-slate-50/80">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-slate-900">
                                {formatOauthUserName(row)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {row.username || "-"} - OAuth #{row.oauth_user_id}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
                                {row.coverage_code || "ALL"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  Number(row.active_flag) === 1
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {Number(row.active_flag) === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500">
                              {formatDateTime(row.updated_at)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-2">
                                {Number(row.active_flag) === 1 ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDeactivateBypassUser(row)}
                                    className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                                  >
                                    Remove
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateBypassStatus(row, 1)}
                                    className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 hover:text-emerald-800"
                                  >
                                    Activate
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(isCreateOpen || isEditOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  System Settings V2
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  {isCreateOpen ? "Create Setting" : "Edit Setting"}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {formError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {formError}
              </div>
            )}
            {formSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {formSuccess}
              </div>
            )}

            <form onSubmit={handleSave} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Key
                </label>
                <input
                  value={formData.key}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, key: event.target.value }))
                  }
                  type="text"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Value
                </label>
                {isPositiveIntegerSetting ? (
                  <input
                    value={formData.value}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, value: event.target.value }))
                    }
                    type="number"
                    min="1"
                    step="1"
                    max={enrollmentSettingHelp ? 2147483647 : undefined}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  />
                ) : (
                  <textarea
                    value={formData.value}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, value: event.target.value }))
                    }
                    rows={4}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  />
                )}
                {isPrnSearchMaxReferencesSetting && (
                  <p className="mt-2 text-xs text-slate-500">
                    Whole number only. Default: 500. Example: 400.
                  </p>
                )}
                {enrollmentSettingHelp && (
                  <p className="mt-2 text-xs text-slate-500">{enrollmentSettingHelp} Changes apply to the next upload or processing run.</p>
                )}
                {formData.key.trim() ===
                  "claims_receiving_batch_allowed_coverages" && (
                  <p className="mt-2 text-xs text-slate-500">
                    Example JSON: ["OUTPATIENT-OPD-OR", "INPATIENT-ADMISSION"]
                  </p>
                )}
                {formData.key.trim() ===
                  "claims_processing_loa_exempt_coverages" && (
                  <p className="mt-2 text-xs text-slate-500">
                    Example JSON: ["OUTPATIENT-BASIC-CONSULTATION", "OUTPATIENT-REFERRAL-TO-SPECIALIST", "MATERNITY-BASIC-CONSULTATION"]
                  </p>
                )}
                {formData.key.trim() ===
                  "claims_receiving_batch_excluded_rules" && (
                  <div className="mt-2 space-y-1 text-xs text-slate-500">
                    <p>Example JSON:</p>
                    <pre className="block whitespace-pre-wrap rounded-lg bg-slate-100 px-3 py-2 text-[11px] text-slate-700">
                      {`[
  {"coverage_code":"INPATIENT-ADMISSION","claim_status":"EXTRACTED"},
  {"coverage_code":"OUTPATIENT-OPD-OR","claim_status":"EXTRACTED"}
]`}
                    </pre>
                    <p className="text-slate-400">
                      Use uppercase values. Matching rows will be Receive-only and
                      excluded from batch creation.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Description
                </label>
                <input
                  value={formData.description}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  type="text"
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-100 transition hover:bg-indigo-700"
                >
                  {isSaving ? "Saving..." : "Save Setting"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettingsV2;
